/**
 * The templates the phone renders forms from.
 *
 * This used to be two TypeScript constants and nothing else, with a note saying
 * `getTemplate()` could swap to the server "when the backend comes online". The
 * backend has been online for a while: templates are authored in the web
 * builder, pulled by `applyTemplate`, and written to the device's `templates`
 * table — and then ignored, because every form surface reads the constants.
 *
 * That gap was not cosmetic. It is the whole vertical-pack argument: one core,
 * many domain configurations, a new vertical shipped by authoring a template
 * rather than by an engineer. As built, adding a vertical meant editing a
 * constant in this file and cutting a release, which is the opposite claim.
 *
 * So the constants stay, demoted to a seed, and the cache below is what
 * `getTemplate()` actually reads.
 *
 * **Synchronous on purpose.** Every caller is a React render — the form screen,
 * the field editor, the suggestion sheet, the review screen, the inbox — and a
 * render cannot await. Making the getter async would have meant reworking seven
 * call sites and introducing a loading state into screens that currently have
 * none, to fetch rows that are already on the device. A cache filled once at
 * boot and again after each pull keeps every call site exactly as it was.
 */
import { all } from '@kapture/sync-core';
import { FormTemplate, type FormTemplate as FormTemplateT } from '@kapture/types';

const BRIDGE_V4_2 = FormTemplate.parse({
  id: 'tpl-bridge-v4-2',
  name: 'Bridge Inspection',
  version: '4.2',
  usageCount: 234,
  // What this customer calls the things a bridge round covers, so grouped
  // evidence reads "Deck panel P3-0414" rather than a bare code. Presentation
  // only: it never asserts the code resolves to anything.
  config: { subjectNoun: 'Deck panel', subjectNounPlural: 'Deck panels' },
  sections: [
    { id: 'context', title: 'Site context', order: 0 },
    { id: 'condition', title: 'Structural condition', order: 1 },
    { id: 'defects', title: 'Defects & risks', order: 2 },
  ],
  fields: [
    {
      id: 'asset-id',
      sectionId: 'context',
      kind: 'text',
      label: 'Asset ID',
      required: true,
      // `scan` turns on the scanner for this field; `pattern` is this
      // customer's code format, checked as a warning rather than a rule — a
      // plate replaced out of sequence still has to be recordable.
      config: {
        placeholder: 'e.g. BR-WB-P3-0414',
        scan: true,
        pattern: '^BR-[A-Z]{2}-P\\d-\\d{4}$',
      },
    },
    {
      id: 'weather',
      sectionId: 'context',
      kind: 'select',
      label: 'Weather',
      required: false,
      config: { options: ['Sunny', 'Overcast', 'Rain', 'Fog'] },
    },
    {
      id: 'access-notes',
      sectionId: 'context',
      kind: 'longText',
      label: 'Access notes',
      required: false,
      config: { placeholder: 'Boat access, road closures, landlord contacts…' },
    },
    {
      id: 'overall-condition',
      sectionId: 'condition',
      kind: 'severity',
      label: 'Overall condition',
      required: true,
      config: {
        options: [
          { value: 'A', label: 'A · As new' },
          { value: 'B', label: 'B · Minor' },
          { value: 'C', label: 'C · Major' },
          { value: 'D', label: 'D · Urgent' },
        ],
      },
    },
    {
      id: 'load-rating-ok',
      sectionId: 'condition',
      kind: 'boolean',
      label: 'Load rating unchanged',
      required: true,
      config: {},
    },
    {
      id: 'defect-summary',
      sectionId: 'defects',
      kind: 'longText',
      label: 'Defect summary',
      required: true,
      config: { placeholder: 'What you found, in your own words.' },
    },
    {
      id: 'defect-width-mm',
      sectionId: 'defects',
      kind: 'number',
      label: 'Widest defect (mm)',
      required: false,
      config: { unit: 'mm', min: 0, step: 0.1 },
    },
    {
      id: 'defect-length-cm',
      sectionId: 'defects',
      kind: 'number',
      label: 'Longest defect (cm)',
      required: false,
      config: { unit: 'cm', min: 0, step: 1 },
    },
    {
      id: 'defect-priority',
      sectionId: 'defects',
      kind: 'select',
      label: 'Priority',
      required: true,
      config: { options: ['Monitor', 'Remediate', 'Immediate'] },
    },
    {
      id: 'survey-date',
      sectionId: 'context',
      kind: 'date',
      label: 'Survey date',
      required: true,
      config: {},
    },
  ],
});

const SITE_WALKTHROUGH = FormTemplate.parse({
  id: 'tpl-site-walkthrough-v1-0',
  name: 'Site walkthrough',
  version: '1.0',
  usageCount: 48,
  sections: [{ id: 'summary', title: 'Summary', order: 0 }],
  fields: [
    {
      id: 'site-ref',
      sectionId: 'summary',
      kind: 'text',
      label: 'Site reference',
      required: true,
      config: {},
    },
    {
      id: 'notes',
      sectionId: 'summary',
      kind: 'longText',
      label: 'Notes',
      required: false,
      config: {},
    },
  ],
});

/**
 * Seed templates.
 *
 * Not dead code and not the catalogue any more. They are what a device renders
 * before its first pull — a fresh install on a plane, or the dev seed — and
 * what the design showcase draws. A server template with the same id wins.
 */
const SEED: FormTemplateT[] = [BRIDGE_V4_2, SITE_WALKTHROUGH];

export const DEFAULT_TEMPLATE_ID = BRIDGE_V4_2.id;

/** Filled by `loadTemplates()`; empty until the first call. */
const cache = new Map<string, FormTemplateT>();

interface TemplateRow {
  id: string;
  name: string;
  definition: string;
}

/**
 * Read the pulled templates off the device and into the cache.
 *
 * Call at boot, and again whenever a pull lands. Failures are swallowed per
 * row: one template authored with a field kind this build does not know must
 * not take the other nine down with it, and the seed is still there to render
 * from. A parse failure is logged rather than thrown for the same reason the
 * capture reader does it — losing one form is a bad day, losing every form is
 * an app that will not open.
 */
export async function loadTemplates(): Promise<number> {
  let rows: TemplateRow[];
  try {
    rows = await all<TemplateRow>(
      `SELECT id, name, definition FROM templates WHERE deleted_at IS NULL`,
    );
  } catch (err) {
    // Before migrations, or on a platform with no database bound yet.
    console.warn('[templates] could not read local templates', err);
    return 0;
  }

  let loaded = 0;
  for (const row of rows) {
    try {
      // `definition` mirrors FormTemplate minus the two that are columns.
      const parsed = FormTemplate.parse({
        ...(JSON.parse(row.definition) as Record<string, unknown>),
        id: row.id,
        name: row.name,
      });
      cache.set(parsed.id, parsed);
      loaded += 1;
    } catch (err) {
      console.error('[templates] skipping unparseable template', row.id, err);
    }
  }
  return loaded;
}

/** Testing seam — drops the cache so a test starts from the seed. */
export function resetTemplateCache(): void {
  cache.clear();
}

export function listTemplates(): FormTemplateT[] {
  if (cache.size === 0) return SEED;
  // Server templates first, then any seed the server has no answer for, so a
  // fresh install that has pulled one template still offers the built-ins.
  const merged = new Map(SEED.map((t) => [t.id, t]));
  for (const [id, t] of cache) merged.set(id, t);
  return [...merged.values()];
}

export function getTemplate(id: string | null | undefined): FormTemplateT | null {
  if (!id) return null;
  // Cache first: a template edited in the web builder should render as edited,
  // not as whatever shipped in this bundle.
  return cache.get(id) ?? SEED.find((t) => t.id === id) ?? null;
}

export function getFieldDef(template: FormTemplateT, fieldId: string) {
  return template.fields.find((f) => f.id === fieldId) ?? null;
}

export function getSectionDef(template: FormTemplateT, sectionId: string) {
  return template.sections.find((s) => s.id === sectionId) ?? null;
}

/** Fields for a section in their declared order. */
export function fieldsForSection(template: FormTemplateT, sectionId: string) {
  return template.fields.filter((f) => f.sectionId === sectionId);
}
